import { createContext, useContext, type ReactNode } from "react";

// Conversation-scoped values that deep descendants need (e.g. artifact
// renderers inside markdown) but don't want to receive via prop drilling.
//
// Exactly one of `conversationId` or `shareToken` is populated:
//   - Owner views set `conversationId` (UUID); artifact fetches use the
//     owner-only endpoint.
//   - Admin reviewer views set `conversationId` and `isAdmin`; artifact
//     fetches use the admin endpoint, since the owner one refuses a
//     cross-user read.
//   - Share-token views set `shareToken`; artifact fetches use the
//     token-scoped previews endpoint, with the shares-table row acting as
//     the authorization.
interface ConversationContextValue {
  conversationId: string | null;
  shareToken: string | null;
  isAdmin: boolean;
}

const ConversationContext = createContext<ConversationContextValue | null>(null);

export const ConversationProvider = ({
  conversationId,
  shareToken = null,
  isAdmin = false,
  children,
}: {
  conversationId: string | null;
  shareToken?: string | null;
  isAdmin?: boolean;
  children: ReactNode;
}) => (
  <ConversationContext.Provider value={{ conversationId, shareToken, isAdmin }}>
    {children}
  </ConversationContext.Provider>
);

export const useConversationContext = (): ConversationContextValue => {
  const ctx = useContext(ConversationContext);
  if (!ctx) {
    throw new Error("useConversationContext must be used within <ConversationProvider>");
  }
  return ctx;
};
